/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/naming-convention */
// deps
import { useContext, useRef, useCallback, useState, useMemo } from "react";

// internal-deps
import {
    ApiContext,
    CheckoutContext,
    CheckoutActionTypes,
    CartOverlaysContext,
    AnalyticsContext,
    recordCustomTrackingNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
} from "@swiggy-private/checkout-core";
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import {
    COUPON_GROUP_TYPES,
    CouponInfo,
    ICouponsAPIResponse,
    CroutonIds,
    CroutonPriority,
    ReviewedCartApiResponse,
    MRONativeLocalCart,
    MROCouponClashPopup,
    CouponMessageCardType,
    CouponMessageCardCTAType,
    CouponMessageCard,
} from "@swiggy-private/cart-types";
import { CouponListingPLWidgetTypes, INextBestCoupon, ICouponCard, ICouponCards } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";

// helpers
import { isCouponAppliedSuccessfully, isCouponNotApplied } from "@cart-client/helpers/Cart";
import { logCouponAppliedEvent } from "@cart-client/helpers/Clevertap";
import { randomStringIdGenerator } from "@swiggy-private/core-ui";
import {
    getCouponSuggestionErrorCard,
    isBestCouponGroup,
    isGreatDealCouponGroup,
} from "@cart-client/helpers/CouponListing";
import { getDiscountingToastMessage } from "@cart-client/helpers/Coupon";
import { impressionCartToast } from "@cart-client/analytics/events";
import {
    getMROCartRxRenderOrder,
    getMROChildCartByRestaurantId,
    getMROChildCarts,
} from "@cart-client/helpers/MROFoodCart";
import { prepareInternalWebLocalCartPayloadFromServerAndLocalCart } from "@cart-client/helpers/MROFoodCartPayloadPrep";
import { applyCouponPublic, fetchCouponsPublic } from "@cart-client/apis/publicCart";
import { removeCouponPublic } from "@cart-client/apis/Cart";

// constants
import { ERROR_TEXT, DISCOUNTING_TOAST_DURATION } from "@cart-client/constants/Coupon";
import { CUSTOM_TRACKING_EVENTS, TEXTS } from "@cart-client/constants/MROConstants";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useMROAnalyticsPayload } from "@cart-client/hooks/useMROAnalyticsPayload";

// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// interfaces
import { IFetchCouponsParams } from "@cart-client/hooks/types";
import { SyncApplyCouponCartParams } from "@cart-client/interfaces/Coupon";
import type { IPLWidgetControllerCards } from "@swiggy-private/dls-react-mxn/dist/WidgetController/PLWidgetController/type";
import { ReviewedCart } from "@swiggy-private/cart-types";
import { CouponListingCouponRemovalPayload, MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";

const useMROFoodCartApplyCoupon = (): {
    syncApplyCouponCart: (params: SyncApplyCouponCartParams) => void;
    showCouponModal: boolean;
    toggleCouponModal: VoidFunction;
    showDelightPopup: boolean;
    togglingDelightPopup: VoidFunction;
    updateCouponState: boolean;
    isFetching: boolean;
    coupons: ICouponCard[] | null;
    isApplyingCoupon: boolean;
    isRemovingCoupon?: boolean;
    errorMessage: string;
    fetchCoupons: ({ restaurantId, cartValue, couponCode, subpage, appliedCouponCodes }: IFetchCouponsParams) => void;
    clearErrorMessage: VoidFunction;
    nextBestCoupon: INextBestCoupon | null;
    resetCouponListingPage: VoidFunction;
    handleCouponListingPageCouponRemove: (payload: CouponListingCouponRemovalPayload) => void;
    tncCoupon?: CouponInfo | null;
    setTncCoupon?: (coupon: CouponInfo | null) => void;
    showPendingDiscountingComms: (restaurantId?: string | number) => void;
    cards: IPLWidgetControllerCards[];
    hasInitiallyFetched: boolean;
    hasApplyCouponInitiated: boolean;
    lastAppliedCouponRestaurantId?: string | number;
    applyCouponClashPopup?: MROCouponClashPopup;
} => {
    const apiContextValue = useContext(ApiContext);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const [updateCouponState, setUpdateCouponState] = useState<boolean>(false);
    const [coupons, setCoupons] = useState<ICouponCard[] | null>(null);
    const [nextBestCoupon, setNextBestCoupon] = useState<INextBestCoupon | null>(null);
    const [cards, setCards] = useState<IPLWidgetControllerCards[]>([]);
    const [isFetching, setFetching] = useState<boolean>(false);
    const hasInitiallyFetched = useRef<boolean>(false);
    const [suggestedCoupons, setSuggestedCoupons] = useState<string[]>([]);
    const [isApplyingCoupon, setApplyingCoupon] = useState<boolean>(false);
    const [isRemovingCoupon, setRemovingCoupon] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [tncCoupon, setTncCoupon] = useState<CouponInfo | null>(null);
    const { cartState, setCartState } = useContext(CartContext);
    const { registerCrouton } = useContext(CartOverlaysContext);
    const { reviewedCart: cartResponse } = cartState;
    const cartRxIdRenderOrder = useMemo(
        () => getMROCartRxRenderOrder(cartResponse as MROReviewedCart),
        [cartResponse],
    );
    const [showCouponModal, setShowCouponModal] = useState<boolean>(false);
    const [showDelightPopup, setShowDelightPopup] = useState<boolean>(false);
    // Tracks which child cart apply-coupon was requested for, so success/error
    // cards can be read from that child (not parent) while the modal is open.
    const [lastAppliedCouponRestaurantId, setLastAppliedCouponRestaurantId] = useState<string | number | undefined>();
    const [applyCouponClashPopup, setApplyCouponClashPopup] = useState<MROCouponClashPopup | undefined>();
    const hasApplyCouponInitiated = useRef<boolean>(false);
    const { analytics: mroAnalytics } = useMROAnalyticsPayload();

    // check if the coupon error card is of type ERROR_CARD_TYPE_CONFLICTING_OFFER_APPLIED and the cta type is CTA_TYPE_APPLY
    const enableApplyCouponClashPopup = useCallback((couponError: CouponMessageCard) => {
        return (
            couponError.type === CouponMessageCardType.ERROR_CARD_TYPE_CONFLICTING_OFFER_APPLIED &&
            couponError.cta?.type === CouponMessageCardCTAType.CTA_TYPE_APPLY
        );
    }, []);

    const apiCallRef = useRef(false);
    const isMountedRef = useMountedRef();

    const apiHost = apiContextValue.host.unwrap();

    const apiSuccess = useCallback(
        (cartResponse: MROReviewedCart, restaurantId?: string | number): CouponMessageCard | undefined => {
            apiCallRef.current = false;
            const childCartFromMROResponse = getMROChildCartByRestaurantId(cartResponse, String(restaurantId));
            if (isMountedRef.current && !isEmpty(childCartFromMROResponse)) {
                // log clevertap event when coupon applied successfully
                if (isCouponAppliedSuccessfully(childCartFromMROResponse as unknown as ReviewedCartApiResponse)) {
                    logCouponAppliedEvent({ data: childCartFromMROResponse } as unknown as ReviewedCart);
                    recordCustomTrackingNREvent({
                        action: CUSTOM_TRACKING_EVENTS.COUPON_FLOW_SUCCESS,
                        timestamp: Date.now(),
                        sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
                        statusCode: childCartFromMROResponse?.statusCode,
                        statusMessage: childCartFromMROResponse?.statusMessage,
                        couponCode: childCartFromMROResponse?.coupon_code,
                    });
                }

                if (isCouponNotApplied(childCartFromMROResponse as unknown as ReviewedCartApiResponse)) {
                    return childCartFromMROResponse.coupon_details?.coupon_error_card;
                } else if (!childCartFromMROResponse.coupon_code) {
                    // This block will execute when a coupon is successfully removed
                    recordCustomTrackingNREvent({
                        action: CUSTOM_TRACKING_EVENTS.COUPON_FLOW_REMOVAL_SUCCESS,
                        timestamp: Date.now(),
                        sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
                        statusCode: childCartFromMROResponse?.statusCode,
                        statusMessage: childCartFromMROResponse?.statusMessage,
                    });
                }

                // update the cart state with the new cart data
                setCartState({
                    type: CartActionTypes.UPDATE_CART,
                    payload: {
                        uniqueIdForCart: randomStringIdGenerator(),
                        localCart: prepareInternalWebLocalCartPayloadFromServerAndLocalCart(
                            cartResponse as MROReviewedCart,
                            cartState?.localCart as MRONativeLocalCart,
                        ),
                        reviewedCart: cartResponse as MROReviewedCart,
                    },
                });
            }
        },
        [isMountedRef, setCartState, cartState?.localCart],
    );

    const clearErrorMessage = useCallback(() => {
        setErrorMessage("");
    }, []);

    const resetCouponListingPage = useCallback(() => {
        setApplyingCoupon(false);
        clearErrorMessage();
        setSuggestedCoupons([]);
        setCoupons(null);
        setFetching(false);
        setNextBestCoupon(null);
        setApplyCouponClashPopup(undefined);
        hasApplyCouponInitiated.current = false;
        hasInitiallyFetched.current = false;
    }, [clearErrorMessage]);

    const apiFailure = useCallback(
        (err: unknown, isCouponListingFlow?: boolean) => {
            recordCustomTrackingNREvent({
                action: CUSTOM_TRACKING_EVENTS.COUPON_FLOW_FAILURE,
                errorStack: err,
                timestamp: Date.now(),
                isCouponListingFlow,
                sid: GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid),
            });
            apiCallRef.current = false;

            if (isMountedRef.current) {
                captureError(err);
                setUpdateCouponState(false);
                setShowCouponModal(false);
                setLastAppliedCouponRestaurantId(undefined);
                if (isCouponListingFlow) {
                    setErrorMessage(ERROR_TEXT.DEFAULT);
                    setApplyingCoupon(false);
                }
            }
        },
        [isMountedRef],
    );

    const findNextBestCoupon = useCallback(
        (couponCode: string): INextBestCoupon | null => {
            let couponToSuggest: CouponInfo | undefined;
            let type: COUPON_GROUP_TYPES | null = null;
            const alreadySuggested = suggestedCoupons.find((each: string) => each.includes(couponCode));
            if (!alreadySuggested && nextBestCoupon) {
                return nextBestCoupon;
            }
            const bestCoupon = coupons?.find((coupon) => isBestCouponGroup(coupon.type));
            const greatDealCoupon = coupons?.find((coupon) => isGreatDealCouponGroup(coupon.type));
            const couponGroupToSuggest = bestCoupon || greatDealCoupon;
            if (couponGroupToSuggest) {
                const coupon = couponGroupToSuggest.coupons?.find((coupon: CouponInfo) => {
                    const id = couponGroupToSuggest.type + "_" + coupon.title;
                    return !suggestedCoupons.includes(id);
                });
                if (coupon) {
                    const couponId = couponGroupToSuggest.type + "_" + coupon.title;
                    couponToSuggest = coupon;
                    type = couponGroupToSuggest.type;
                    setSuggestedCoupons((prev) => [...prev, couponId]);
                }
            }
            return type && couponToSuggest ? { type, coupon: couponToSuggest } : null;
        },
        [nextBestCoupon, coupons, suggestedCoupons],
    );

    const showApplyCouponClashPopup = useCallback(
        (couponError: CouponMessageCard, applyFallbackHandler: () => void, restaurantId?: string | number) => {
            setApplyCouponClashPopup({
                enabled: enableApplyCouponClashPopup(couponError),
                title: couponError.title,
                subtitle: couponError.description,
                secondaryCta: {
                    text: TEXTS.CANCEL,
                    onClick: () => {
                        mroAnalytics.logClickMROCouponClashPopup(String(restaurantId), TEXTS.CANCEL);
                        setApplyCouponClashPopup(undefined);
                    },
                },
                primaryCta: {
                    text: couponError.cta?.text,
                    onClick: () => {
                        mroAnalytics.logClickMROCouponClashPopup(String(restaurantId), couponError.cta?.text || "");
                        setApplyCouponClashPopup(undefined);
                        applyFallbackHandler();
                    },
                },
                impressionEvent: () => {
                    mroAnalytics.logImpressionMROCouponClashPopup(String(restaurantId));
                },
            });
        },
        [enableApplyCouponClashPopup, mroAnalytics],
    );

    const syncApplyCouponCart = useCallback(
        ({
            couponCode,
            isCouponListingFlow,
            restaurantId,
            isUserConsentProvidedForMROSwitch = false,
        }: {
            couponCode?: string;
            isCouponListingFlow?: boolean;
            restaurantId?: string | number;
            isUserConsentProvidedForMROSwitch?: boolean;
        }) => {
            if (!isMountedRef.current || updateCouponState || apiCallRef.current) {
                return;
            }

            if (isEmpty(couponCode) || isEmpty(restaurantId)) {
                apiFailure(new Error("MRO Coupon code and restaurant id are required"));
                return;
            }

            apiCallRef.current = true;
            setApplyingCoupon(true);
            setUpdateCouponState(true);
            setApplyCouponClashPopup(undefined);
            setLastAppliedCouponRestaurantId(restaurantId);

            applyCouponPublic<MROReviewedCart>(apiHost, couponCode, {
                restaurantId,
                isUserConsentProvidedForMROSwitch,
                cartRxIdRenderOrder,
            })
                .then((cartResponse: MROReviewedCart) => {
                    return apiSuccess(cartResponse, restaurantId);
                })
                .then((couponError?: CouponMessageCard) => {
                    if (isMountedRef.current) {
                        hasApplyCouponInitiated.current = true;
                        if (isCouponListingFlow) {
                            if (!couponError) {
                                setShowCouponModal(true);
                                setUpdateCouponState(false);
                                resetCouponListingPage();
                                setCheckoutState({
                                    type: CheckoutActionTypes.SHOW_MRO_FOOD_CART,
                                    payload: {
                                        skipCartReload: true,
                                    },
                                });
                            } else {
                                if (enableApplyCouponClashPopup(couponError)) {
                                    setApplyingCoupon(false);
                                    setUpdateCouponState(false);
                                    const applyFallbackHandler = () => {
                                        syncApplyCouponCart({
                                            couponCode,
                                            restaurantId,
                                            isCouponListingFlow,
                                            isUserConsentProvidedForMROSwitch: true,
                                        });
                                    };
                                    showApplyCouponClashPopup(couponError, applyFallbackHandler, restaurantId);
                                    return;
                                }
                                setLastAppliedCouponRestaurantId(undefined);
                                const nextBest = findNextBestCoupon(couponCode);
                                const errorMessage = getCouponSuggestionErrorCard(couponError, nextBest);
                                setErrorMessage(errorMessage);
                                setNextBestCoupon(nextBest);
                                setApplyingCoupon(false);
                                setUpdateCouponState(false);
                            }
                        } else if (couponError && enableApplyCouponClashPopup(couponError)) {
                            // Cart-page direct apply can hit the same MRO coupon clash
                            // as listing; reuse the clash popup + consent re-apply path.
                            setApplyingCoupon(false);
                            setUpdateCouponState(false);
                            const applyFallbackHandler = () => {
                                syncApplyCouponCart({
                                    couponCode,
                                    restaurantId,
                                    isUserConsentProvidedForMROSwitch: true,
                                });
                            };
                            showApplyCouponClashPopup(couponError, applyFallbackHandler, restaurantId);
                        } else {
                            setShowCouponModal(true);
                            setUpdateCouponState(false);
                        }
                    }
                })
                .catch(apiFailure);
        },
        [
            isMountedRef,
            updateCouponState,
            apiHost,
            apiFailure,
            apiSuccess,
            resetCouponListingPage,
            setCheckoutState,
            enableApplyCouponClashPopup,
            findNextBestCoupon,
            showApplyCouponClashPopup,
            cartRxIdRenderOrder,
        ],
    );

    const showPendingDiscountingComms = useCallback(
        (restaurantId?: string | number) => {
            if (isEmpty(restaurantId)) {
                return;
            }
            const childCartFromMROResponse = getMROChildCartByRestaurantId(
                cartResponse as MROReviewedCart,
                String(restaurantId),
            );
            const toastMessage = getDiscountingToastMessage(
                childCartFromMROResponse as unknown as ReviewedCartApiResponse,
            );
            if (!isEmpty(toastMessage)) {
                registerCrouton({
                    content: {
                        title: toastMessage,
                        durationOfCrouton: DISCOUNTING_TOAST_DURATION,
                        impressionEvent: () => {
                            impressionCartToast(
                                JSON.stringify({ msg: toastMessage }),
                                `${cartState?.reviewedCart?.data?.cartId}`,
                                impressionEvent,
                            );
                        },
                    },
                    id: CroutonIds.DISCOUNTING_TOAST,
                    priority: CroutonPriority.PRIORITY_ORDER_THREE,
                });
            }
        },
        [cartResponse, cartState?.reviewedCart?.data?.cartId, impressionEvent, registerCrouton],
    );

    const toggleCouponModal = useCallback(() => {
        // Shown once: open after apply success, close clears the child-cart pointer
        // so a later cart refresh cannot reuse a stale coupon_success_card.
        setShowCouponModal((wasOpen) => {
            if (wasOpen) {
                setLastAppliedCouponRestaurantId(undefined);
            }
            return !wasOpen;
        });
    }, []);

    const togglingDelightPopup = useCallback(() => {
        setShowDelightPopup((showDelightPopup) => !showDelightPopup);
    }, []);

    const extractCouponCardsFromResponse = useCallback((response: ICouponsAPIResponse) => {
        const filterCouponCardsFromResponse = (response?.success?.cards as unknown as ICouponCards[])?.filter(
            (each: ICouponCards) => each?.card?.card?.["@type"] === CouponListingPLWidgetTypes.COUPON_CARD,
        ) as ICouponCards[];
        const couponGroupArr = filterCouponCardsFromResponse?.map((each: ICouponCards) => each?.card?.card);
        couponGroupArr && setCoupons(couponGroupArr);
    }, []);

    const extractPLWidgetsFromResponse = useCallback(
        (response: ICouponsAPIResponse) => {
            extractCouponCardsFromResponse(response);
            setCards(response.success?.cards || []);
        },
        [extractCouponCardsFromResponse],
    );

    const fetchCoupons = useCallback(
        ({ restaurantId, cartValue, couponCode, subpage, childCartCount }: IFetchCouponsParams) => {
            if (isEmpty(restaurantId) || cartValue === undefined) {
                return;
            }

            setFetching(true);
            hasInitiallyFetched.current = true;
            fetchCouponsPublic({
                apiHost,
                cartValue: String(cartValue),
                restaurantId,
                couponCode,
                subpage,
                isNewPORFlow: true,
                childCartCount,
            })
                .then((response: ICouponsAPIResponse) => {
                    setFetching(false);
                    extractPLWidgetsFromResponse(response);
                })
                .catch(() => setFetching(false));
        },
        [apiHost, extractPLWidgetsFromResponse],
    );

    const handleCouponListingPageCouponRemove = useCallback(
        async ({ subpage, couponCode, restaurantId }: CouponListingCouponRemovalPayload) => {
            if (updateCouponState || apiCallRef.current) {
                return;
            }

            if (isEmpty(couponCode) || isEmpty(restaurantId)) {
                apiFailure(new Error("MRO Coupon code and restaurant id are required"));
                return;
            }

            apiCallRef.current = true;
            setRemovingCoupon(true);
            setUpdateCouponState(true);
            setFetching(true);
            setApplyCouponClashPopup(undefined);
            try {
                const response = await removeCouponPublic<MROReviewedCart>(
                    apiHost,
                    couponCode,
                    restaurantId,
                    cartRxIdRenderOrder,
                );
                apiSuccess(response, restaurantId);
                setUpdateCouponState(false);
                if (!isEmpty(response)) {
                    const cartTotal = response?.data?.cart_total_shown_to_coupon || 0;
                    const childCartCount = getMROChildCarts(response).length;
                    if (!isEmpty(restaurantId)) {
                        hasInitiallyFetched.current = true;
                        const response = await fetchCouponsPublic({
                            apiHost,
                            cartValue: String(cartTotal),
                            restaurantId: String(restaurantId),
                            subpage,
                            isNewPORFlow: true,
                            childCartCount,
                        });

                        extractPLWidgetsFromResponse(response);
                    }
                }
                setFetching(false);
                setRemovingCoupon(false);
            } catch (err) {
                setFetching(false);
                setRemovingCoupon(false);
                apiFailure(err, true);
            }
        },
        [apiFailure, apiHost, apiSuccess, extractPLWidgetsFromResponse, updateCouponState, cartRxIdRenderOrder],
    );

    return {
        fetchCoupons,
        syncApplyCouponCart,
        showCouponModal,
        toggleCouponModal,
        updateCouponState,
        coupons,
        isFetching,
        isApplyingCoupon,
        isRemovingCoupon,
        errorMessage,
        clearErrorMessage,
        nextBestCoupon,
        resetCouponListingPage,
        togglingDelightPopup,
        showDelightPopup,
        handleCouponListingPageCouponRemove,
        tncCoupon,
        setTncCoupon,
        showPendingDiscountingComms,
        cards,
        // Indicates whether coupon fetch API has been called at least once.
        // Used to prevent showing "no coupons available" UI before initial fetch completes as well as show no coupons UI after initial fetch completes and data received is empty.
        hasInitiallyFetched: hasInitiallyFetched.current,
        hasApplyCouponInitiated: hasApplyCouponInitiated.current,
        lastAppliedCouponRestaurantId,
        applyCouponClashPopup,
    };
};

export default useMROFoodCartApplyCoupon;
